import React from 'react';
import {
  Inventory,
  ExpandMore,
  ExpandLess,
  DeleteOutline as DeleteIcon,
  KeyboardArrowUp,
  KeyboardArrowDown,
} from '@mui/icons-material';
import { FormControl, Select, MenuItem, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  useInventoryItemsStep,
  InventoryItemsStepProps,
  ProductUnit,
} from './InventoryItemsStep.hooks';
import ProductForm from '../ProductForm';
import { ConfirmationModal as GlobalConfirmationModal } from '../../../ui/Modal';
import { APP_CURRENCY } from '../../../../utils/constants/currency';
import { FlagSwitch } from '../../../../components/ui';
import { useLocaleDecimalInput } from '../../../../hooks/useLocaleDecimalInput';
import {
  Container,
  EmptyState,
  EmptyStateText,
  SummaryStats,
  AddBarCard,
  AddBarFormArea,
  AddBarButton,
  ProductGroupsContainer,
  ProductCard,
  ProductHeader,
  ProductHeaderContent,
  ProductInfo,
  MoveButtons,
  ProductIcon,
  ProductDetails,
  ProductName,
  ProductCode,
  ProductActions,
  HeaderQuantityField,
  HeaderQuantityLabel,
  HeaderQuantityInput,
  HeaderQuantityStepButton,
  ResetButton,
  SectionDeleteButton,
  ExpandIcon,
  UnitPaginationBar,
  UnitPaginationLabel,
  UnitPaginationButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  HeaderCell,
  HeaderCellWithActions,
  TableBody,
  BodyCell,
  StickyHeaderCell,
  StickyBodyCell,
  RowDeleteButton,
  InputField,
  SelectField,
  inheritedSelectSx,
} from './InventoryItemsStep.styles';

type EditableColumn =
  | 'materialId'
  | 'weight'
  | 'length'
  | 'diameter'
  | 'color'
  | 'workshopCode'
  | 'costPrice'
  | 'salePrice';

const ColumnHeader: React.FC<{
  label: string;
  checked: boolean;
  onToggle: () => void;
  width?: string;
}> = ({ label, checked, onToggle, width }) => (
  <HeaderCellWithActions style={width ? { width } : undefined}>
    <div>
      <span>{label}</span>
      <FlagSwitch
        size="small"
        checked={checked}
        onChange={(_e, next) => {
          if (next !== checked) onToggle();
        }}
      />
    </div>
  </HeaderCellWithActions>
);

const DecimalInputField: React.FC<{
  value: string | number;
  onChange: (value: string) => void;
  decimalPlaces?: number;
  min?: number;
  max?: number;
  disabled?: boolean;
}> = ({ value, onChange, decimalPlaces = 2, min, max, disabled }) => {
  const {
    displayValue,
    onFocus,
    onBlur,
    onChange: handleChange,
  } = useLocaleDecimalInput({
    value,
    onChange: (val) => onChange(val != null ? String(val) : ''),
    decimalPlaces,
    min,
    max,
  });

  return (
    <InputField
      type="text"
      inputMode="decimal"
      value={displayValue}
      onFocus={onFocus}
      onBlur={onBlur}
      onChange={(e) => handleChange(e.target.value)}
      disabled={disabled}
    />
  );
};

/** Tooltip del sistema (MUI). El span permite mostrarlo también sobre botones deshabilitados. */
const ActionTooltip: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Tooltip title={title} arrow>
    <span style={{ display: 'inline-flex' }}>{children}</span>
  </Tooltip>
);

const InventoryItemsStep: React.FC<InventoryItemsStepProps> = (props) => {
  const theme = useTheme();
  const {
    materials,
    colors,
    productGroups,
    expandedProductId,
    toggleProductExpansion,
    editableColumns,
    toggleColumnEditable,
    productUnitsPageSize,
    getUnitPageSlice,
    setUnitPage,
    calculateMargin,
    handleUnitChange,
    onRemoveUnit,
    onResetSection,
    handleHeaderQuantityChange,
    handleHeaderQuantityStep,
    onMoveSection,
    draftItem,
    currentProductIds,
    handleDraftUpdate,
    isDraftValid,
    isAddingProduct,
    handleAddFromDraft,
    sectionPendingDelete,
    requestRemoveSection,
    confirmRemoveSection,
    cancelRemoveSection,
  } = useInventoryItemsStep(props);

  const isColumnEditable = (productId: number, column: EditableColumn) =>
    editableColumns[productId]?.[column] ?? false;

  const compactSelectSx = {
    fontSize: '0.75rem',
    '& .MuiSelect-select': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      py: 0.5,
    },
  } as const;

  return (
    <Container>
      <AddBarCard>
        <AddBarFormArea>
          <ProductForm
            item={draftItem}
            index={0}
            products={props.products as any}
            canRemove={false}
            onRemove={() => {}}
            onUpdate={handleDraftUpdate as any}
            selectedProductIds={currentProductIds}
            bare
          />
        </AddBarFormArea>
      </AddBarCard>
      <AddBarButton
        type="button"
        disabled={!isDraftValid || isAddingProduct}
        onClick={handleAddFromDraft}
      >
        {isAddingProduct ? 'Agregando…' : '+ Agregar producto'}
      </AddBarButton>

      {productGroups.length > 0 && (
        <SummaryStats>
          {productGroups.length} {productGroups.length === 1 ? 'producto' : 'productos'} ·{' '}
          {productGroups.reduce((sum, g) => sum + g.totalQuantity, 0)} piezas
        </SummaryStats>
      )}

      {productGroups.length === 0 ? (
        <EmptyState>
          <EmptyStateText>Aún no agregó productos. Use el buscador de arriba.</EmptyStateText>
        </EmptyState>
      ) : (
        <ProductGroupsContainer>
          {productGroups.map((group, groupIndex) => {
            const isExpanded = expandedProductId === group.productId;
            const pageSlice = getUnitPageSlice(group.productId, group.units);
            const showUnitPagination = group.totalQuantity > productUnitsPageSize;
            const colorType = groupIndex % 2 === 0 ? 'blue' : 'green';

            return (
              <ProductCard key={group.productId}>
                <ProductHeader
                  gradientType={colorType}
                  onClick={() => toggleProductExpansion(group.productId)}
                >
                  <ProductHeaderContent>
                    <ProductInfo>
                      <MoveButtons onClick={(e) => e.stopPropagation()}>
                        <ActionTooltip title="Subir">
                          <HeaderQuantityStepButton
                            type="button"
                            colorType={colorType}
                            disabled={groupIndex === 0}
                            onClick={() => onMoveSection(group.productId, -1)}
                            aria-label={`Subir ${group.productName}`}
                          >
                            <KeyboardArrowUp style={{ fontSize: 18 }} />
                          </HeaderQuantityStepButton>
                        </ActionTooltip>
                        <ActionTooltip title="Bajar">
                          <HeaderQuantityStepButton
                            type="button"
                            colorType={colorType}
                            disabled={groupIndex === productGroups.length - 1}
                            onClick={() => onMoveSection(group.productId, 1)}
                            aria-label={`Bajar ${group.productName}`}
                          >
                            <KeyboardArrowDown style={{ fontSize: 18 }} />
                          </HeaderQuantityStepButton>
                        </ActionTooltip>
                      </MoveButtons>
                      <ProductIcon colorType={colorType}>
                        <Inventory style={{ fontSize: 18 }} />
                      </ProductIcon>
                      <ProductDetails>
                        <ProductName>
                          {groupIndex + 1}. {group.productName}
                        </ProductName>
                        <ProductCode>
                          Código: {group.productCode} • {group.totalQuantity}{' '}
                          {group.totalQuantity === 1 ? 'pieza' : 'piezas'}
                        </ProductCode>
                      </ProductDetails>
                    </ProductInfo>
                    <ProductActions onClick={(e) => e.stopPropagation()}>
                      {isExpanded && (
                        <HeaderQuantityField>
                          <HeaderQuantityLabel>Cant.</HeaderQuantityLabel>
                          <ActionTooltip title="Quitar una pieza">
                            <HeaderQuantityStepButton
                              type="button"
                              colorType={colorType}
                              disabled={group.totalQuantity <= 1}
                              onClick={() => handleHeaderQuantityStep(group.productId, -1)}
                              aria-label={`Quitar una pieza de ${group.productName}`}
                            >
                              −
                            </HeaderQuantityStepButton>
                          </ActionTooltip>
                          <HeaderQuantityInput
                            type="number"
                            min={1}
                            defaultValue={group.totalQuantity}
                            key={`${group.productId}-${group.totalQuantity}`}
                            onBlur={(e) =>
                              handleHeaderQuantityChange(group.productId, e.target.value)
                            }
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                            }}
                          />
                          <ActionTooltip title="Agregar una pieza">
                            <HeaderQuantityStepButton
                              type="button"
                              colorType={colorType}
                              onClick={() => handleHeaderQuantityStep(group.productId, 1)}
                              aria-label={`Agregar una pieza a ${group.productName}`}
                            >
                              +
                            </HeaderQuantityStepButton>
                          </ActionTooltip>
                        </HeaderQuantityField>
                      )}

                      {isExpanded && showUnitPagination && (
                        <UnitPaginationBar>
                          <UnitPaginationButton
                            type="button"
                            colorType={colorType}
                            disabled={pageSlice.page <= 1}
                            onClick={() => setUnitPage(group.productId, pageSlice.page - 1)}
                            aria-label="Página anterior de piezas"
                          >
                            ‹
                          </UnitPaginationButton>
                          <UnitPaginationLabel>
                            {pageSlice.pageStart}-{pageSlice.pageEnd} / {pageSlice.totalUnits}
                          </UnitPaginationLabel>
                          <UnitPaginationButton
                            type="button"
                            colorType={colorType}
                            disabled={pageSlice.page >= pageSlice.totalPages}
                            onClick={() => setUnitPage(group.productId, pageSlice.page + 1)}
                            aria-label="Página siguiente de piezas"
                          >
                            ›
                          </UnitPaginationButton>
                        </UnitPaginationBar>
                      )}

                      {isExpanded && (
                        <ActionTooltip title="Restablecer a valores por defecto del producto padre">
                          <ResetButton
                            colorType={colorType}
                            onClick={() => onResetSection(group.productId)}
                          >
                            Restablecer
                          </ResetButton>
                        </ActionTooltip>
                      )}

                      <ActionTooltip title="Quitar este producto y todas sus piezas">
                        <SectionDeleteButton
                          type="button"
                          onClick={() => requestRemoveSection(group.productId)}
                          aria-label={`Quitar ${group.productName}`}
                        >
                          <DeleteIcon fontSize="small" />
                        </SectionDeleteButton>
                      </ActionTooltip>

                      <ActionTooltip title={isExpanded ? 'Contraer' : 'Expandir'}>
                        <ExpandIcon
                          type="button"
                          colorType={colorType}
                          onClick={() => toggleProductExpansion(group.productId)}
                          aria-label={`${isExpanded ? 'Contraer' : 'Expandir'} ${group.productName}`}
                          aria-expanded={isExpanded}
                        >
                          {isExpanded ? (
                            <ExpandLess style={{ fontSize: 22 }} />
                          ) : (
                            <ExpandMore style={{ fontSize: 22 }} />
                          )}
                        </ExpandIcon>
                      </ActionTooltip>
                    </ProductActions>
                  </ProductHeaderContent>
                </ProductHeader>

                {isExpanded && (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <StickyHeaderCell>#</StickyHeaderCell>
                          <ColumnHeader
                            label="Material"
                            width="73px"
                            checked={isColumnEditable(group.productId, 'materialId')}
                            onToggle={() => toggleColumnEditable(group.productId, 'materialId')}
                          />
                          <ColumnHeader
                            label="Peso (g)"
                            width="57px"
                            checked={isColumnEditable(group.productId, 'weight')}
                            onToggle={() => toggleColumnEditable(group.productId, 'weight')}
                          />
                          <ColumnHeader
                            label="Largo (cm)"
                            width="57px"
                            checked={isColumnEditable(group.productId, 'length')}
                            onToggle={() => toggleColumnEditable(group.productId, 'length')}
                          />
                          <ColumnHeader
                            label="Talla"
                            width="53px"
                            checked={isColumnEditable(group.productId, 'diameter')}
                            onToggle={() => toggleColumnEditable(group.productId, 'diameter')}
                          />
                          <ColumnHeader
                            label="Color"
                            width="73px"
                            checked={isColumnEditable(group.productId, 'color')}
                            onToggle={() => toggleColumnEditable(group.productId, 'color')}
                          />
                          <ColumnHeader
                            label="Cód. proveedor"
                            width="73px"
                            checked={isColumnEditable(group.productId, 'workshopCode')}
                            onToggle={() => toggleColumnEditable(group.productId, 'workshopCode')}
                          />
                          <ColumnHeader
                            label={`Costo (${APP_CURRENCY.symbol})`}
                            width="68px"
                            checked={isColumnEditable(group.productId, 'costPrice')}
                            onToggle={() => toggleColumnEditable(group.productId, 'costPrice')}
                          />
                          <ColumnHeader
                            label={`Venta (${APP_CURRENCY.symbol})`}
                            width="68px"
                            checked={isColumnEditable(group.productId, 'salePrice')}
                            onToggle={() => toggleColumnEditable(group.productId, 'salePrice')}
                          />
                          <HeaderCell style={{ width: '58px', textAlign: 'center' }}>
                            Margen %
                          </HeaderCell>
                          <HeaderCell style={{ width: '32px', textAlign: 'center' }}> </HeaderCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pageSlice.units.map((unit: ProductUnit, unitIndex: number) => {
                          const displayIndex = pageSlice.pageStart + unitIndex;
                          const margin = calculateMargin(
                            unit.costs.costPrice,
                            unit.costs.salePrice,
                          );

                          return (
                            <TableRow key={unit.id}>
                              <StickyBodyCell>{displayIndex}</StickyBodyCell>
                              <BodyCell style={{ width: '73px', maxWidth: '73px' }}>
                                <SelectField>
                                  <FormControl
                                    fullWidth
                                    size="small"
                                    sx={{ minWidth: 0, maxWidth: '100%' }}
                                  >
                                    <Select
                                      value={String(unit.properties.materialId ?? '')}
                                      onChange={(e) =>
                                        handleUnitChange(
                                          unit.id,
                                          'properties',
                                          'materialId',
                                          e.target.value as string,
                                        )
                                      }
                                      displayEmpty
                                      size="small"
                                      disabled={!isColumnEditable(unit.productId, 'materialId')}
                                      sx={{
                                        ...compactSelectSx,
                                        ...(!isColumnEditable(unit.productId, 'materialId')
                                          ? inheritedSelectSx(theme)
                                          : {}),
                                      }}
                                    >
                                      <MenuItem value="">
                                        <em>Seleccione</em>
                                      </MenuItem>
                                      {materials.map((material) => (
                                        <MenuItem key={material.id} value={material.id.toString()}>
                                          {material.name}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </SelectField>
                              </BodyCell>
                              <BodyCell style={{ width: '57px', maxWidth: '57px' }}>
                                <DecimalInputField
                                  value={unit.properties.weight}
                                  onChange={(nextValue) =>
                                    handleUnitChange(unit.id, 'properties', 'weight', nextValue)
                                  }
                                  min={0}
                                  disabled={!isColumnEditable(unit.productId, 'weight')}
                                />
                              </BodyCell>
                              <BodyCell style={{ width: '57px', maxWidth: '57px' }}>
                                <DecimalInputField
                                  value={unit.properties.length}
                                  onChange={(nextValue) =>
                                    handleUnitChange(unit.id, 'properties', 'length', nextValue)
                                  }
                                  min={0}
                                  disabled={!isColumnEditable(unit.productId, 'length')}
                                />
                              </BodyCell>
                              <BodyCell style={{ width: '53px', maxWidth: '53px' }}>
                                <InputField
                                  type="text"
                                  value={unit.properties.diameter}
                                  onChange={(e) =>
                                    handleUnitChange(
                                      unit.id,
                                      'properties',
                                      'diameter',
                                      e.target.value,
                                    )
                                  }
                                  disabled={!isColumnEditable(unit.productId, 'diameter')}
                                  placeholder="Ej. XL"
                                />
                              </BodyCell>
                              <BodyCell style={{ width: '73px', maxWidth: '73px' }}>
                                <FormControl fullWidth size="small">
                                  <Select
                                    value={unit.properties.color}
                                    displayEmpty
                                    onChange={(e) =>
                                      handleUnitChange(
                                        unit.id,
                                        'properties',
                                        'color',
                                        e.target.value,
                                      )
                                    }
                                    disabled={!isColumnEditable(unit.productId, 'color')}
                                    sx={{
                                      ...compactSelectSx,
                                      ...(!isColumnEditable(unit.productId, 'color')
                                        ? inheritedSelectSx(theme)
                                        : {}),
                                    }}
                                  >
                                    <MenuItem value="">
                                      <em>Seleccione</em>
                                    </MenuItem>
                                    {colors.map((color) => (
                                      <MenuItem key={color.id} value={color.name}>
                                        {color.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </BodyCell>
                              <BodyCell style={{ width: '73px', maxWidth: '73px' }}>
                                <InputField
                                  type="text"
                                  value={unit.properties.workshopCode}
                                  onChange={(e) =>
                                    handleUnitChange(
                                      unit.id,
                                      'properties',
                                      'workshopCode',
                                      e.target.value,
                                    )
                                  }
                                  disabled={!isColumnEditable(unit.productId, 'workshopCode')}
                                />
                              </BodyCell>
                              <BodyCell style={{ width: '68px', maxWidth: '68px' }}>
                                <DecimalInputField
                                  value={unit.costs.costPrice}
                                  onChange={(nextValue) =>
                                    handleUnitChange(unit.id, 'costs', 'costPrice', nextValue)
                                  }
                                  min={0.01}
                                  max={999999.99}
                                  disabled={!isColumnEditable(unit.productId, 'costPrice')}
                                />
                              </BodyCell>
                              <BodyCell style={{ width: '68px', maxWidth: '68px' }}>
                                <DecimalInputField
                                  value={unit.costs.salePrice}
                                  onChange={(nextValue) =>
                                    handleUnitChange(unit.id, 'costs', 'salePrice', nextValue)
                                  }
                                  min={0}
                                  max={999999.99}
                                  disabled={!isColumnEditable(unit.productId, 'salePrice')}
                                />
                              </BodyCell>
                              <BodyCell style={{ width: '58px', textAlign: 'center' }}>
                                <span
                                  style={{
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    color:
                                      parseFloat(margin) > 30
                                        ? '#059669'
                                        : parseFloat(margin) < 15
                                          ? '#dc2626'
                                          : '#6b7280',
                                  }}
                                >
                                  {margin}%
                                </span>
                              </BodyCell>
                              <BodyCell style={{ width: '32px', textAlign: 'center' }}>
                                <ActionTooltip title="Quitar esta pieza">
                                  <RowDeleteButton
                                    type="button"
                                    onClick={() => onRemoveUnit(unit.id)}
                                    aria-label={`Quitar pieza ${displayIndex}`}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </RowDeleteButton>
                                </ActionTooltip>
                              </BodyCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </ProductCard>
            );
          })}
        </ProductGroupsContainer>
      )}

      <GlobalConfirmationModal
        open={sectionPendingDelete != null}
        onClose={cancelRemoveSection}
        onConfirm={confirmRemoveSection}
        title="Quitar producto"
        message={
          sectionPendingDelete != null
            ? `Se quitarán todas las piezas de "${
                productGroups.find((g) => g.productId === sectionPendingDelete)?.productName ??
                String(sectionPendingDelete)
              }". Esta acción no se puede deshacer.`
            : ''
        }
        confirmText="Quitar"
        cancelText="Cancelar"
      />
    </Container>
  );
};

export default InventoryItemsStep;
